import { useMemo, useState, useCallback, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import {
  ChevronDown,
  ChevronRight,
  ImageOff,
  Paperclip,
  Loader2,
  ExternalLink,
  Plus,
  MapPin,
  Building2,
  FlaskConical,
  Wrench,
  Beaker,
} from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { Badge } from '@/shared/ui/badge'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { MultiSelectCombobox } from '@/shared/ui/multi-select-combobox'
import type { MultiSelectOption } from '@/shared/ui/multi-select-combobox'
import { ComplianceBadge } from '@/shared/ui/compliance-badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { t } from '@/shared/lib/i18n'
import { matchesComplianceFilter, type ComplianceFilter } from '@/shared/lib/compliance'
import {
  adminInstrumentTypesPath,
  cloneAdminInstrumentTypePath,
  editAdminInstrumentTypePath,
  newAdminInstrumentTypePath,
} from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import { InstrumentTypeAttachmentsManager } from '@/features/instrument-type-attachments/InstrumentTypeAttachmentsManager'
import {
  InstrumentFormDialog,
  type InstrumentFormOptions,
} from '@/features/instrument-form/InstrumentFormDialog'
import {
  RETURN_TO_INSTRUMENT_TYPES,
  appendReturnTo,
  type InstrumentTypesReturnFilters,
} from '@/shared/lib/instrument-edit-return-to'
import type { WebAdminInstrumentType, WebAdminInstrumentTypeInstrument } from '@/shared/types/web'
import { useHydrateLocationFilters } from '@/shared/hooks/useHydrateLocationFilters'
import { clearLocationFilters, writeLocationFilters } from '@/shared/lib/locationFiltersSession'
import type { SharedProps } from '@/shared/types/SharedProps'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'

interface SelectOption {
  value: number
  label: string
}

interface DepartmentOption {
  value: number
  label: string
  siteId: number | null
}

interface UnitOption {
  value: number
  label: string
  siteId: number | null
  departmentId: number | null
}

interface Filters {
  search: string
  siteIds: string
  departmentIds: string
  unitIds: string
  tagIds: string
  expandedTypeId: string
}

interface Props {
  instrumentTypes: WebAdminInstrumentType[]
  filters: Filters
  expandedTypeInstruments: WebAdminInstrumentTypeInstrument[] | null
  instrumentFormOptions: InstrumentFormOptions
  parentDaySchedule?: WeekdaySchedule
  siteOptions: SelectOption[]
  departmentOptions: DepartmentOption[]
  unitOptions: UnitOption[]
  tagOptions: MultiSelectOption[]
  [key: string]: unknown
}

const idsFromCsv = (csv: string) => (csv ? csv.split(',').map(Number).filter(Boolean) : [])

const InstrumentTypesIndexPage = () => {
  const {
    instrumentTypes,
    filters,
    expandedTypeInstruments,
    instrumentFormOptions,
    siteOptions,
    departmentOptions,
    unitOptions,
    tagOptions,
  } = usePage<Props>().props
  const { complianceSettings, currentTenantId, currentUserInfo } = usePage<SharedProps>().props
  const currentUserId = currentUserInfo?.id ?? null

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.instrumentTypes') }])

  const [search, setSearch] = useState(filters.search)
  const [siteIds, setSiteIds] = useState<number[]>(idsFromCsv(filters.siteIds))
  const [departmentIds, setDepartmentIds] = useState<number[]>(idsFromCsv(filters.departmentIds))
  const [unitIds, setUnitIds] = useState<number[]>(idsFromCsv(filters.unitIds))
  const [tagIds, setTagIds] = useState<number[]>(idsFromCsv(filters.tagIds))
  const [withInstrumentsOpen, setWithInstrumentsOpen] = useState(true)
  const [withoutInstrumentsOpen, setWithoutInstrumentsOpen] = useState(false)
  const [complianceSort, setComplianceSort] = useState<'asc' | 'desc' | null>(null)
  const [complianceFilter, setComplianceFilter] = useState<ComplianceFilter>('all')
  const [attachmentsDialogType, setAttachmentsDialogType] = useState<WebAdminInstrumentType | null>(
    null,
  )
  // Restores the type row the user had expanded before navigating away to edit an
  // instrument, so Cancel/Save don't collapse it (REBUILD-388/JW 386) — the server already
  // computes expandedTypeInstruments for this id on a plain page load, not just a partial
  // reload, so no extra fetch is needed here.
  const [expandedTypeId, setExpandedTypeId] = useState<number | null>(
    filters.expandedTypeId ? Number(filters.expandedTypeId) : null,
  )
  const [instrumentsLoading, setInstrumentsLoading] = useState(false)
  const [addInstrumentType, setAddInstrumentType] = useState<WebAdminInstrumentType | null>(null)
  const [cloneDialogType, setCloneDialogType] = useState<WebAdminInstrumentType | null>(null)
  const [cloneName, setCloneName] = useState('')
  const [cloning, setCloning] = useState(false)

  const reloadGenerationRef = useRef(0)
  const reloadCancelTokenRef = useRef<{ cancel: VoidFunction } | null>(null)

  // Server round-trip: search/filters must match the SAME permission-scoped data the
  // with/without-instruments split and instrument counts already use, so filtering can't be
  // a client-side Array#filter over the already-loaded instrumentTypes prop (Dev Notes).
  const reload = useCallback(
    (
      overrides: {
        search?: string
        siteIds?: number[]
        departmentIds?: number[]
        unitIds?: number[]
        tagIds?: number[]
      } = {},
    ) => {
      // Keep the expanded type's instrument list showing a loading state while filters
      // change too, not just on the initial expand — otherwise it flashes stale-then-updated.
      if (expandedTypeId !== null) setInstrumentsLoading(true)
      const effectiveSiteIds = overrides.siteIds ?? siteIds
      const effectiveDeptIds = overrides.departmentIds ?? departmentIds
      const effectiveUnitIds = overrides.unitIds ?? unitIds
      writeLocationFilters(currentTenantId, currentUserId, {
        siteIds: effectiveSiteIds,
        departmentIds: effectiveDeptIds,
        unitIds: effectiveUnitIds,
      })
      const generation = reloadGenerationRef.current + 1
      reloadGenerationRef.current = generation
      // router.reload is async and same-page, so Inertia does not interrupt the previous
      // filter reload; cancel it so a slower earlier response cannot win.
      reloadCancelTokenRef.current?.cancel()
      router.reload({
        only: ['instrumentTypes', 'expandedTypeInstruments', 'filters'],
        // Inertia's router.reload always sets preserveState and preserveScroll, and its
        // ReloadOptions type omits those flags. The open dropdown stays mounted.
        onCancelToken: (cancelToken) => {
          reloadCancelTokenRef.current = cancelToken
        },
        data: {
          search: overrides.search ?? search,
          site_ids: effectiveSiteIds.join(','),
          department_ids: effectiveDeptIds.join(','),
          unit_ids: effectiveUnitIds.join(','),
          tag_ids: (overrides.tagIds ?? tagIds).join(','),
          expanded_type_id: expandedTypeId,
        },
        onFinish: () => {
          if (reloadGenerationRef.current === generation) setInstrumentsLoading(false)
        },
      })
    },
    [
      search,
      siteIds,
      departmentIds,
      unitIds,
      tagIds,
      expandedTypeId,
      currentTenantId,
      currentUserId,
    ],
  )

  const debouncedReload = useDebouncedCallback(reload, 400)

  useHydrateLocationFilters({
    tenantId: currentTenantId,
    userId: currentUserId,
    hasUrlLocationParams:
      idsFromCsv(filters.siteIds).length > 0 ||
      idsFromCsv(filters.departmentIds).length > 0 ||
      idsFromCsv(filters.unitIds).length > 0,
    currentFilters: {
      siteIds: idsFromCsv(filters.siteIds),
      departmentIds: idsFromCsv(filters.departmentIds),
      unitIds: idsFromCsv(filters.unitIds),
    },
    siteOptions,
    departmentOptions,
    unitOptions,
    onHydrate: (hydrated) => {
      setSiteIds(hydrated.siteIds)
      setDepartmentIds(hydrated.departmentIds)
      setUnitIds(hydrated.unitIds)
      reload({
        siteIds: hydrated.siteIds,
        departmentIds: hydrated.departmentIds,
        unitIds: hydrated.unitIds,
      })
    },
  })

  // Carried by "Edit instrument" and "Add Instrument" links/forms so a Cancel or Save from
  // there returns here with the same filters and expanded type instead of a reset index
  // (REBUILD-388/JW 386).
  const currentReturnFilters: InstrumentTypesReturnFilters = useMemo(
    () => ({
      search,
      siteIds: siteIds.join(','),
      departmentIds: departmentIds.join(','),
      unitIds: unitIds.join(','),
      tagIds: tagIds.join(','),
      expandedTypeId: expandedTypeId != null ? String(expandedTypeId) : '',
    }),
    [search, siteIds, departmentIds, unitIds, tagIds, expandedTypeId],
  )

  const handleSearchChange = (value: string) => {
    setSearch(value)
    debouncedReload({ search: value })
  }

  const activeFilterCount = [
    search,
    siteIds.length > 0,
    departmentIds.length > 0,
    unitIds.length > 0,
    tagIds.length > 0,
    complianceFilter !== 'all',
    complianceSort !== null,
  ].filter(Boolean).length

  const clearFilters = () => {
    debouncedReload.cancel()
    reloadCancelTokenRef.current?.cancel()
    setSearch('')
    setSiteIds([])
    setDepartmentIds([])
    setUnitIds([])
    setTagIds([])
    setComplianceFilter('all')
    setComplianceSort(null)
    setExpandedTypeId(null)
    clearLocationFilters(currentTenantId, currentUserId)
    router.visit(adminInstrumentTypesPath(), { preserveState: false })
  }

  // Lazy per-type instrument list (mirrors legacy click-to-load): expanding a type triggers
  // a partial reload that fetches only `expandedTypeInstruments` for that type. Collapsing
  // (or expanding another) clears the current list so a stale one is never shown.
  const toggleTypeInstruments = useCallback(
    (typeId: number) => {
      if (expandedTypeId === typeId) {
        setExpandedTypeId(null)
        return
      }
      setExpandedTypeId(typeId)
      setInstrumentsLoading(true)
      router.reload({
        only: ['expandedTypeInstruments'],
        data: { expanded_type_id: typeId },
        onFinish: () => setInstrumentsLoading(false),
      })
    },
    [expandedTypeId],
  )

  // Clone requires confirmation with an editable name (REBUILD-388/JW 386) — cloning used
  // to fire immediately on click with no way to review or adjust the "(Copy)" name, and
  // there is no delete action to undo an accidental clone.
  const openCloneDialog = (type: WebAdminInstrumentType) => {
    setCloneDialogType(type)
    setCloneName(`${type.name} (Copy)`)
  }

  const handleConfirmClone = () => {
    if (!cloneDialogType) return

    setCloning(true)
    router.post(
      cloneAdminInstrumentTypePath(cloneDialogType.id),
      { name: cloneName.trim() },
      {
        preserveScroll: true,
        onSuccess: () => setCloneDialogType(null),
        onFinish: () => setCloning(false),
      },
    )
  }

  const canWrite = useCan(Resources.ADMIN_INSTRUMENT_TYPE, Actions.CREATE)
  const canCreateInstrument = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.CREATE)
  const canUpdateInstrument = useCan(Resources.INSTRUMENT_MAINTENANCE, Actions.UPDATE)
  const canCreateAttachment = useCan(Resources.ATTACHMENT, Actions.CREATE)
  const canDeleteAttachment = useCan(Resources.ATTACHMENT, Actions.DELETE)

  // Site→Dept→Unit cascade: grey out (not remove) invalid options based on the current
  // parent selection — copied from Instruments/Index.tsx rather than a shared hook (Dev Notes).
  const departmentOptionsWithDisabled = useMemo(
    () =>
      departmentOptions.map((dept) => ({
        ...dept,
        disabled: siteIds.length > 0 && !siteIds.includes(dept.siteId ?? -1),
      })),
    [departmentOptions, siteIds],
  )

  const unitOptionsWithDisabled = useMemo(
    () =>
      unitOptions.map((unit) => ({
        ...unit,
        disabled:
          (siteIds.length > 0 && !siteIds.includes(unit.siteId ?? -1)) ||
          (departmentIds.length > 0 && !departmentIds.includes(unit.departmentId ?? -1)),
      })),
    [unitOptions, siteIds, departmentIds],
  )

  // Prune already-selected child filters that are no longer valid when the parent
  // selection narrows, otherwise a stale child filter would silently over-restrict results.
  const handleSiteCommit = (newSiteIds: number[]) => {
    const validDeptIds = departmentIds.filter((id) => {
      const dept = departmentOptions.find((d) => d.value === id)
      return !dept || newSiteIds.length === 0 || newSiteIds.includes(dept.siteId ?? -1)
    })
    const validUnitIds = unitIds.filter((id) => {
      const unit = unitOptions.find((u) => u.value === id)
      return (
        !unit ||
        ((newSiteIds.length === 0 || newSiteIds.includes(unit.siteId ?? -1)) &&
          (validDeptIds.length === 0 || validDeptIds.includes(unit.departmentId ?? -1)))
      )
    })
    setSiteIds(newSiteIds)
    setDepartmentIds(validDeptIds)
    setUnitIds(validUnitIds)
    reload({ siteIds: newSiteIds, departmentIds: validDeptIds, unitIds: validUnitIds })
  }

  const handleDeptCommit = (newDeptIds: number[]) => {
    const validUnitIds = unitIds.filter((id) => {
      const unit = unitOptions.find((u) => u.value === id)
      return !unit || newDeptIds.length === 0 || newDeptIds.includes(unit.departmentId ?? -1)
    })
    setDepartmentIds(newDeptIds)
    setUnitIds(validUnitIds)
    reload({ departmentIds: newDeptIds, unitIds: validUnitIds })
  }

  const handleUnitCommit = (newUnitIds: number[]) => {
    setUnitIds(newUnitIds)
    reload({ unitIds: newUnitIds })
  }

  const handleTagsCommit = (newTagIds: number[]) => {
    setTagIds(newTagIds)
    reload({ tagIds: newTagIds })
  }

  // The server already returns only types matching search + site/department/unit/tags —
  // only the client-side compliance filter/sort remain applied on top (Dev Notes: not part
  // of this ticket's scope, legacy has no server-side compliance filter either).
  const filtered = useMemo(() => {
    return instrumentTypes.filter((type) =>
      matchesComplianceFilter(type.rollingCompliancePercent, complianceFilter),
    )
  }, [instrumentTypes, complianceFilter])

  const sortByCompliance = useCallback(
    (list: WebAdminInstrumentType[]) => {
      if (!complianceSort) return list
      return [...list].sort((a, b) => {
        const aVal = a.rollingCompliancePercent ?? -1
        const bVal = b.rollingCompliancePercent ?? -1
        return complianceSort === 'asc' ? aVal - bVal : bVal - aVal
      })
    },
    [complianceSort],
  )

  const withInstruments = useMemo(
    () => sortByCompliance(filtered.filter((type) => type.instrumentsCount > 0)),
    [filtered, sortByCompliance],
  )

  const withoutInstruments = useMemo(
    () => sortByCompliance(filtered.filter((type) => type.instrumentsCount === 0)),
    [filtered, sortByCompliance],
  )

  const totalInstruments = useMemo(
    () => withInstruments.reduce((sum, type) => sum + type.instrumentsCount, 0),
    [withInstruments],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('admin.instrumentTypes.pageTitle')}</h1>
        {canWrite && (
          <Button asChild>
            <Link href={newAdminInstrumentTypePath()}>{t('admin.instrumentTypes.newButton')}</Link>
          </Button>
        )}
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            placeholder={t('admin.instrumentTypes.searchPlaceholder')}
            aria-label={t('admin.instrumentTypes.searchPlaceholder')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-72"
          />
          <MultiSelectCombobox
            options={siteOptions}
            selected={siteIds}
            onChange={setSiteIds}
            onClose={handleSiteCommit}
            placeholder={t('admin.instrumentTypes.allSites')}
            searchPlaceholder={t('admin.instrumentTypes.filterSite')}
            className="w-44"
            showBadges={false}
          />
          <MultiSelectCombobox
            options={departmentOptionsWithDisabled}
            selected={departmentIds}
            onChange={setDepartmentIds}
            onClose={handleDeptCommit}
            placeholder={t('admin.instrumentTypes.allDepartments')}
            searchPlaceholder={t('admin.instrumentTypes.filterDepartment')}
            className="w-48"
            showBadges={false}
          />
          <MultiSelectCombobox
            options={unitOptionsWithDisabled}
            selected={unitIds}
            onChange={setUnitIds}
            onClose={handleUnitCommit}
            placeholder={t('admin.instrumentTypes.allUnits')}
            searchPlaceholder={t('admin.instrumentTypes.filterUnit')}
            className="w-40"
            showBadges={false}
          />
          <MultiSelectCombobox
            options={tagOptions}
            selected={tagIds}
            onChange={setTagIds}
            onClose={handleTagsCommit}
            placeholder={t('admin.instrumentTypes.tagsPlaceholder')}
            searchPlaceholder={t('admin.instrumentTypes.filterTags')}
            className="w-44"
            showBadges={false}
          />
          <SearchableSelect
            className="w-52"
            value={complianceFilter}
            onValueChange={(value) => setComplianceFilter(value as ComplianceFilter)}
            placeholder={t('admin.instrumentTypes.filterByCompliance')}
            options={[
              { value: 'all', label: t('admin.instrumentTypes.allCompliance') },
              { value: 'high', label: t('admin.instrumentTypes.complianceHigh') },
              { value: 'medium', label: t('admin.instrumentTypes.complianceMedium') },
              { value: 'low', label: t('admin.instrumentTypes.complianceLow') },
              { value: 'none', label: t('admin.instrumentTypes.complianceNone') },
            ]}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              setComplianceSort((prev) =>
                prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc',
              )
            }
          >
            {t('admin.instrumentTypes.complianceScore')}
            {complianceSort === 'asc' ? ' ↑' : complianceSort === 'desc' ? ' ↓' : ' ↕'}
          </Button>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto text-muted-foreground"
              onClick={clearFilters}
            >
              {t('admin.instrumentTypes.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      <Collapsible open={withoutInstrumentsOpen} onOpenChange={setWithoutInstrumentsOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md border bg-muted/50 px-4 py-3 text-left font-medium hover:bg-muted transition-colors">
          {withoutInstrumentsOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span>
            {t('admin.instrumentTypes.withoutInstrumentsHeader', {
              count: String(withoutInstruments.length),
            })}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {withoutInstruments.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                {t('admin.instrumentTypes.emptyState')}
              </p>
            ) : (
              withoutInstruments.map((type) => (
                <TypeRow
                  key={type.id}
                  type={type}
                  canWrite={canWrite}
                  canCreateInstrument={canCreateInstrument}
                  canUpdateInstrument={canUpdateInstrument}
                  onAddInstrument={setAddInstrumentType}
                  onClone={openCloneDialog}
                  windowDays={complianceSettings?.windowDays}
                  colorCodes={complianceSettings?.colorCodes}
                  onOpenAttachments={setAttachmentsDialogType}
                />
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Collapsible open={withInstrumentsOpen} onOpenChange={setWithInstrumentsOpen}>
        <CollapsibleTrigger className="flex w-full items-center gap-2 rounded-md border bg-muted/50 px-4 py-3 text-left font-medium hover:bg-muted transition-colors">
          {withInstrumentsOpen ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span>
            {t('admin.instrumentTypes.withInstrumentsHeader', {
              count: String(withInstruments.length),
              total: String(totalInstruments),
            })}
          </span>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="mt-2 space-y-2">
            {withInstruments.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">
                {t('admin.instrumentTypes.emptyState')}
              </p>
            ) : (
              withInstruments.map((type) => (
                <TypeRow
                  key={type.id}
                  type={type}
                  canWrite={canWrite}
                  canCreateInstrument={canCreateInstrument}
                  canUpdateInstrument={canUpdateInstrument}
                  onAddInstrument={setAddInstrumentType}
                  onClone={openCloneDialog}
                  showBreakdown
                  windowDays={complianceSettings?.windowDays}
                  colorCodes={complianceSettings?.colorCodes}
                  onOpenAttachments={setAttachmentsDialogType}
                  expanded={expandedTypeId === type.id}
                  instruments={expandedTypeId === type.id ? expandedTypeInstruments : null}
                  instrumentsLoading={expandedTypeId === type.id && instrumentsLoading}
                  onToggleInstruments={toggleTypeInstruments}
                  returnFilters={currentReturnFilters}
                />
              ))
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      <Dialog
        open={attachmentsDialogType !== null}
        onOpenChange={(open) => !open && setAttachmentsDialogType(null)}
      >
        <DialogContent className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('admin.instrumentTypes.attachments.dialogTitle', {
                name: attachmentsDialogType?.name ?? '',
              })}
            </DialogTitle>
          </DialogHeader>
          {attachmentsDialogType && (
            <InstrumentTypeAttachmentsManager
              instrumentTypeId={attachmentsDialogType.id}
              canCreate={canCreateAttachment}
              canDelete={canDeleteAttachment}
              onChanged={() => router.reload({ only: ['instrumentTypes'] })}
            />
          )}
        </DialogContent>
      </Dialog>

      {addInstrumentType && (
        <InstrumentFormDialog
          open={addInstrumentType !== null}
          onOpenChange={(open) => !open && setAddInstrumentType(null)}
          instrumentTypeId={addInstrumentType.id}
          instrumentTypeName={addInstrumentType.name}
          options={instrumentFormOptions}
          returnTo="instrument_types"
          returnFilters={currentReturnFilters}
        />
      )}

      <Dialog
        open={cloneDialogType !== null}
        onOpenChange={(open) => !open && setCloneDialogType(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('admin.instrumentTypes.cloneDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.instrumentTypes.cloneDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="clone-instrument-type-name">
              {t('admin.instrumentTypes.cloneDialog.nameLabel')}
            </Label>
            <Input
              id="clone-instrument-type-name"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setCloneDialogType(null)}>
              {t('admin.instrumentTypes.cloneDialog.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleConfirmClone}
              disabled={cloning || cloneName.trim().length === 0}
            >
              {t('admin.instrumentTypes.cloneDialog.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface TypeRowProps {
  type: WebAdminInstrumentType
  canWrite: boolean
  canCreateInstrument: boolean
  canUpdateInstrument: boolean
  onAddInstrument: (type: WebAdminInstrumentType) => void
  onClone: (type: WebAdminInstrumentType) => void
  onOpenAttachments: (type: WebAdminInstrumentType) => void
  showBreakdown?: boolean
  windowDays?: number
  colorCodes?: import('@/shared/types/SharedProps').ComplianceColorBand[]
  expanded?: boolean
  instruments?: WebAdminInstrumentTypeInstrument[] | null
  instrumentsLoading?: boolean
  onToggleInstruments?: (typeId: number) => void
  returnFilters?: InstrumentTypesReturnFilters
}

const TypeRow = ({
  type,
  canWrite,
  canCreateInstrument,
  canUpdateInstrument,
  onAddInstrument,
  onClone,
  onOpenAttachments,
  showBreakdown = false,
  windowDays,
  colorCodes,
  expanded = false,
  instruments = null,
  instrumentsLoading = false,
  onToggleInstruments,
  returnFilters,
}: TypeRowProps) => (
  <div className="rounded-lg border bg-card shadow-sm">
    <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        {type.imgUrl ? (
          <img src={type.imgUrl} alt="" className="h-10 w-10 shrink-0 rounded object-cover" />
        ) : (
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-muted">
            <ImageOff className="h-4 w-4 text-muted-foreground" />
          </span>
        )}
        <div className="min-w-0">
          <span className="font-semibold block truncate">{type.name}</span>
          <p className="text-sm text-muted-foreground truncate">
            {[type.manufacturerName, type.manufacturerModelName].filter(Boolean).join(' · ') || '—'}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <button
              type="button"
              onClick={() => onOpenAttachments(type)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {t('admin.instrumentTypes.attachmentsCount', { count: String(type.attachmentCount) })}
            </button>
            <ComplianceBadge
              percent={type.rollingCompliancePercent}
              windowDays={windowDays}
              colorCodes={colorCodes}
            />
          </div>
          {showBreakdown && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">
                {t('admin.instrumentTypes.instrumentsCount', {
                  count: String(type.instrumentsCount),
                })}
              </span>
              {type.siteBreakdown.length > 0 && (
                <>
                  <span className="text-border">·</span>
                  <span className="font-medium text-foreground">
                    {t('admin.instrumentTypes.sitesLabel')}
                  </span>
                  {type.siteBreakdown.map((entry) => (
                    <Badge key={entry.siteName} variant="secondary" className="text-xs font-normal">
                      {entry.siteName}: {entry.count}
                    </Badge>
                  ))}
                </>
              )}
            </div>
          )}
          {showBreakdown && onToggleInstruments && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onToggleInstruments(type.id)}
              aria-expanded={expanded}
              className="mt-2"
            >
              <ChevronDown
                className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`}
              />
              {expanded
                ? t('admin.instrumentTypes.instrumentList.toggleHide')
                : t('admin.instrumentTypes.instrumentList.toggleShow')}
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {type.active ? (
          <Badge variant="outline" className="text-green-700 border-green-300">
            {t('admin.instrumentTypes.activeColumn')}
          </Badge>
        ) : (
          <Badge variant="outline" className="text-red-700 border-red-300">
            {t('admin.instrumentTypes.inactiveLabel')}
          </Badge>
        )}
        {canWrite && (
          <>
            <Button variant="outline" size="sm" onClick={() => onClone(type)}>
              {t('admin.instrumentTypes.cloneButton')}
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href={editAdminInstrumentTypePath(type.id)}>
                {t('admin.instrumentTypes.editButton')}
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>

    {canCreateInstrument && (
      <div className="flex border-t px-3 py-2">
        <Button variant="outline" size="sm" onClick={() => onAddInstrument(type)}>
          <Plus className="h-4 w-4" />
          {t('admin.instrumentTypes.instrumentList.addInstrument')}
        </Button>
      </div>
    )}

    {expanded && (
      <div className="border-t px-3 py-3">
        {instrumentsLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('admin.instrumentTypes.instrumentList.loading')}
          </div>
        ) : !instruments || instruments.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t('admin.instrumentTypes.instrumentList.empty')}
          </p>
        ) : (
          <ol className="space-y-3">
            {instruments.map((instrument, index) => (
              <InstrumentListItem
                key={instrument.id}
                instrument={instrument}
                index={index + 1}
                canUpdateInstrument={canUpdateInstrument}
                returnFilters={returnFilters}
              />
            ))}
          </ol>
        )}
      </div>
    )}
  </div>
)

interface InstrumentListItemProps {
  instrument: WebAdminInstrumentTypeInstrument
  index: number
  canUpdateInstrument: boolean
  returnFilters?: InstrumentTypesReturnFilters
}

export const InstrumentListItem = ({
  instrument,
  index,
  canUpdateInstrument,
  returnFilters,
}: InstrumentListItemProps) => (
  <li className="rounded-lg border bg-background p-4 shadow-sm transition-colors hover:border-primary/40">
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
          {index}
        </span>
        <span className="truncate font-semibold">{instrument.instrumentLabel}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {instrument.statusLabel ? (
          <Badge variant="secondary">{instrument.statusLabel}</Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground">
            {t('admin.instrumentTypes.instrumentList.noStatus')}
          </Badge>
        )}
        {canUpdateInstrument && (
          <>
            <Button asChild variant="outline" size="sm">
              <Link
                href={appendReturnTo(
                  instrument.editInstrumentHref,
                  RETURN_TO_INSTRUMENT_TYPES,
                  returnFilters,
                )}
              >
                {t('admin.instrumentTypes.instrumentList.editButton')}
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href={instrument.cloneInstrumentHref}>
                {t('admin.instrumentTypes.instrumentList.cloneButton')}
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>

    <div className="mt-3 flex flex-col gap-1.5 text-sm">
      <Field icon={MapPin} label={t('admin.instrumentTypes.instrumentList.site')}>
        {instrument.siteName ?? '—'}
      </Field>
      <Field icon={Building2} label={t('admin.instrumentTypes.instrumentList.department')}>
        {instrument.departmentName ?? '—'}
      </Field>
      <Field icon={FlaskConical} label={t('admin.instrumentTypes.instrumentList.unit')}>
        {instrument.unitName ?? '—'}
      </Field>
    </div>

    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      <span>
        {t('admin.instrumentTypes.instrumentList.ellId')}:{' '}
        <span className="font-mono text-foreground">{instrument.id}</span>
      </span>
      <span>
        {t('admin.instrumentTypes.instrumentList.serialNumber')}:{' '}
        <span className="font-mono text-foreground">{instrument.serialNumber ?? '—'}</span>
      </span>
    </div>

    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t pt-3 text-sm">
      <Link
        href={instrument.viewInstrumentHref}
        className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        {t('admin.instrumentTypes.instrumentList.viewInstrument')}
      </Link>
      <Link
        href={instrument.viewMaintenanceLogsHref}
        className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
      >
        <Wrench className="h-3.5 w-3.5" />
        {t('admin.instrumentTypes.instrumentList.viewMaintenanceLogs')}
      </Link>
      <Link
        href={instrument.controlsReagentsHref}
        className="inline-flex items-center gap-1 font-medium text-primary underline-offset-2 hover:underline"
      >
        <Beaker className="h-3.5 w-3.5" />
        {t('admin.instrumentTypes.instrumentList.controlsReagents')}
      </Link>
    </div>
  </li>
)

const Field = ({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  children: React.ReactNode
}) => (
  <div className="flex items-center gap-1.5">
    <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
    <span className="text-muted-foreground">{label}:</span>
    <span className="font-medium">{children}</span>
  </div>
)

export default InstrumentTypesIndexPage
